import { useEffect, useRef, useState, useCallback } from "react";
import { api, formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ChefHat, Upload, FileText, ImageIcon, IdCard, ShieldCheck, Trash2, Check, Plus, Eye, Pencil } from "lucide-react";
import { openAuthedInNewTab, fetchAuthedBlobUrl } from "@/lib/authFile";

const API = process.env.REACT_APP_BACKEND_URL;
const DOC_TYPES = [
  { id: "photo",             label: "Profile photo",             icon: ImageIcon, accept: "image/*" },
  { id: "aadhaar_front",     label: "Aadhaar card — front",      icon: IdCard,    accept: "image/*,application/pdf" },
  { id: "aadhaar_back",      label: "Aadhaar card — back",       icon: IdCard,    accept: "image/*,application/pdf" },
  { id: "pan",               label: "PAN card",                  icon: IdCard,    accept: "image/*,application/pdf" },
  { id: "employment_letter", label: "Employment letter",         icon: FileText,  accept: "image/*,application/pdf" },
  { id: "bgv",               label: "Background Verification (BGV)", icon: ShieldCheck, accept: "image/*,application/pdf" },
];

function DocSlot({ chef, doc, onUploaded, onDeleted }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const meta = chef.documents?.[doc.id];
  const Icon = doc.icon;

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/chefs/${chef.id}/documents/${doc.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${doc.label} uploaded`);
      onUploaded && onUploaded();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Remove ${doc.label}?`)) return;
    try {
      await api.delete(`/admin/chefs/${chef.id}/documents/${doc.id}`);
      toast.success("Removed");
      onDeleted && onDeleted();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); }
  };

  return (
    <div className="p-4 rounded-2xl bg-[#f5f5f7] border border-black/[0.05]" data-testid={`doc-${doc.id}`}>
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${meta ? "bg-emerald-500 text-white" : "bg-white text-[#1d1d1f]/60"}`}>
          {meta ? <Check size={18}/> : <Icon size={16}/>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold tracking-tight">{doc.label}</div>
          {meta ? (
            <div className="text-[11px] text-[#1d1d1f]/60 truncate">{meta.filename} · {(meta.size / 1024).toFixed(1)} KB</div>
          ) : (
            <div className="text-[11px] text-[#1d1d1f]/60">Not uploaded</div>
          )}
        </div>
      </div>

      <input
        ref={inputRef} type="file" accept={doc.accept} className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }}
        data-testid={`doc-input-${doc.id}`}
      />

      <div className="mt-3 flex gap-1.5">
        <Button
          size="sm" variant="outline" disabled={busy}
          className="rounded-full text-xs bg-white hover:bg-white/80 flex-1"
          onClick={() => inputRef.current?.click()}
          data-testid={`doc-upload-${doc.id}`}
        >
          <Upload size={12} className="mr-1"/> {meta ? "Replace" : "Upload"}
        </Button>
        {meta && (
          <>
            <Button
              size="sm" variant="outline"
              className="rounded-full text-xs bg-white"
              title="View"
              onClick={async () => {
                const ok = await openAuthedInNewTab(`/admin/chefs/${chef.id}/documents/${doc.id}`);
                if (!ok) toast.error("Couldn't open document — please try uploading again.");
              }}
              data-testid={`doc-view-${doc.id}`}
            >
              <Eye size={12}/>
            </Button>
            <Button size="sm" variant="outline" className="rounded-full text-xs bg-white hover:bg-rose-50 text-rose-600" onClick={remove} data-testid={`doc-remove-${doc.id}`}>
              <Trash2 size={12}/>
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

function FilePick({ label, file, setFile, accept, testid }) {
  return (
    <div className="rounded-xl bg-[#f5f5f7] border border-black/[0.05] p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[13px] font-semibold tracking-tight">{label} <span className="text-rose-500">*</span></div>
          <div className="text-[11px] text-[#1d1d1f]/60 truncate">{file ? `${file.name} · ${(file.size/1024).toFixed(1)} KB` : "Required"}</div>
        </div>
        <label className="shrink-0">
          <input type="file" accept={accept} className="hidden" onChange={e => setFile(e.target.files?.[0] || null)} data-testid={testid}/>
          <span className={`inline-flex items-center cursor-pointer rounded-full px-3 py-1.5 text-[11px] font-medium ${file ? "bg-emerald-500 text-white" : "bg-white border border-black/[0.08]"}`}>
            {file ? <><Check size={11} className="mr-1"/>Selected</> : <><Upload size={11} className="mr-1"/>Pick file</>}
          </span>
        </label>
      </div>
    </div>
  );
}

function OptionalFilePick({ label, file, setFile, accept, testid }) {
  return (
    <div className="rounded-xl bg-white border border-dashed border-black/[0.15] p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[13px] font-semibold tracking-tight">{label} <span className="text-[10px] uppercase tracking-wider text-[#1d1d1f]/50 ml-1">Optional</span></div>
          <div className="text-[11px] text-[#1d1d1f]/60 truncate">{file ? `${file.name} · ${(file.size/1024).toFixed(1)} KB` : "Skip for now or attach"}</div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {file && (
            <button
              type="button"
              onClick={() => setFile(null)}
              className="text-[10px] uppercase tracking-wider text-[#1d1d1f]/60 hover:text-rose-600"
              data-testid={`${testid}-clear`}
            >Clear</button>
          )}
          <label>
            <input type="file" accept={accept} className="hidden" onChange={e => setFile(e.target.files?.[0] || null)} data-testid={testid}/>
            <span className={`inline-flex items-center cursor-pointer rounded-full px-3 py-1.5 text-[11px] font-medium ${file ? "bg-emerald-500 text-white" : "bg-white border border-black/[0.15]"}`}>
              {file ? <><Check size={11} className="mr-1"/>Selected</> : <><Upload size={11} className="mr-1"/>Pick file</>}
            </span>
          </label>
        </div>
      </div>
    </div>
  );
}

function AddChefDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ name: "", phone: "", specialties: "", experience_years: "", notes: "" });
  const [busy, setBusy] = useState(false);
  // Required document files — must be uploaded inline during creation
  const [photoFile, setPhotoFile] = useState(null);
  const [aadhaarFrontFile, setAadhaarFrontFile] = useState(null);
  const [aadhaarBackFile, setAadhaarBackFile] = useState(null);
  const [panFile, setPanFile] = useState(null);
  const [empFile, setEmpFile] = useState(null);
  // Optional document
  const [bgvFile, setBgvFile] = useState(null);

  const reset = () => {
    setForm({ name: "", phone: "", specialties: "", experience_years: "", notes: "" });
    setPhotoFile(null); setAadhaarFrontFile(null); setAadhaarBackFile(null);
    setPanFile(null); setEmpFile(null); setBgvFile(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    // Required: name, phone (>=10 digits), specialties, experience.
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Name and mobile are required"); return;
    }
    const phoneDigits = form.phone.replace(/\D/g, "");
    if (phoneDigits.length < 10) {
      toast.error("Mobile must contain at least 10 digits"); return;
    }
    if (!form.specialties.trim()) { toast.error("At least one specialty required"); return; }
    if (form.experience_years === "" || Number(form.experience_years) < 0) {
      toast.error("Years of experience required"); return;
    }
    if (!photoFile || !empFile || !aadhaarFrontFile || !aadhaarBackFile || !panFile) {
      toast.error("All documents are mandatory: photo, both sides of Aadhaar, PAN, employment letter");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        // No email field on this form — backend will synthesise `<digits>@chef.delcochefs.local`.
        address: "",
        notes: form.notes.trim(),
        specialties: form.specialties.split(",").map(s => s.trim()).filter(Boolean),
        experience_years: Number(form.experience_years),
      };
      const { data: chef } = await api.post("/admin/chefs", payload);
      const upload = async (docId, file) => {
        const fd = new FormData(); fd.append("file", file);
        await api.post(`/admin/chefs/${chef.id}/documents/${docId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      };
      const uploads = [
        upload("photo", photoFile),
        upload("aadhaar_front", aadhaarFrontFile),
        upload("aadhaar_back", aadhaarBackFile),
        upload("pan", panFile),
        upload("employment_letter", empFile),
      ];
      if (bgvFile) uploads.push(upload("bgv", bgvFile));
      await Promise.all(uploads);
      toast.success(`${chef.name} added${bgvFile ? " with BGV" : ""}`);
      reset();
      onCreated && onCreated(chef);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't add chef"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o)=>{ if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-chef-dialog">
        <DialogHeader>
          <DialogTitle>Add chef</DialogTitle>
          <DialogDescription>Required: name, mobile, specialties, experience + 5 documents. BGV is optional. Max 8 MB per file.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="add-chef-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Full name <span className="text-rose-500">*</span></Label><Input required value={form.name} onChange={e=>setForm({...form, name: e.target.value})} data-testid="chef-name"/></div>
            <div><Label>Mobile <span className="text-rose-500">*</span></Label>
              <Input
                required
                value={form.phone}
                onChange={e => setForm({...form, phone: e.target.value.replace(/[^\d+]/g, "").slice(0, 15)})}
                onKeyDown={e => { if (!/^[\d+]$/.test(e.key) && !["Backspace","Delete","Tab","ArrowLeft","ArrowRight","Home","End"].includes(e.key)) e.preventDefault(); }}
                placeholder="+91 98765 43210"
                inputMode="tel"
                data-testid="chef-phone"/>
            </div>
            <div><Label>Years of experience <span className="text-rose-500">*</span></Label><Input required type="number" min={0} max={60} value={form.experience_years} onChange={e=>setForm({...form, experience_years: e.target.value})} data-testid="chef-exp"/></div>
            <div><Label>Specialties <span className="text-rose-500">*</span></Label><Input required value={form.specialties} onChange={e=>setForm({...form, specialties: e.target.value})} placeholder="North Indian, Punjabi" data-testid="chef-specialties"/></div>
            <div className="sm:col-span-2"><Label>Notes</Label><Textarea value={form.notes} onChange={e=>setForm({...form, notes: e.target.value})} data-testid="chef-notes"/></div>
          </div>

          <div className="pt-2 border-t border-black/[0.06]">
            <div className="text-sm font-semibold tracking-tight mb-2">Required documents</div>
            <div className="grid sm:grid-cols-2 gap-2">
              <FilePick label="Profile photo" file={photoFile} setFile={setPhotoFile} accept="image/*" testid="chef-photo-input"/>
              <FilePick label="Employment letter" file={empFile} setFile={setEmpFile} accept="image/*,application/pdf" testid="chef-emp-input"/>
              <FilePick label="Aadhaar — front side" file={aadhaarFrontFile} setFile={setAadhaarFrontFile} accept="image/*,application/pdf" testid="chef-aadhaar-front-input"/>
              <FilePick label="Aadhaar — back side"  file={aadhaarBackFile}  setFile={setAadhaarBackFile}  accept="image/*,application/pdf" testid="chef-aadhaar-back-input"/>
              <FilePick label="PAN card" file={panFile} setFile={setPanFile} accept="image/*,application/pdf" testid="chef-pan-input"/>
            </div>
            <div className="text-sm font-semibold tracking-tight mt-4 mb-2">
              Optional document <span className="text-[#1d1d1f]/50 font-normal">— upload later from Manage documents if not ready now</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              <OptionalFilePick label="Background Verification (BGV)" file={bgvFile} setFile={setBgvFile} accept="image/*,application/pdf" testid="chef-bgv-input"/>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" className="rounded-full" onClick={() => { reset(); onOpenChange(false); }}>Cancel</Button>
            <Button type="submit" disabled={busy} className="rounded-full bg-[#0071e3] hover:bg-[#0077ed]" data-testid="chef-submit">{busy ? "Saving..." : "Create chef"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}


// ---- Edit chef dialog (audit-logged patch to /admin/chefs/{id}) ----
function EditChefDialog({ chef, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!chef) { setForm(null); return; }
    // Hide synthesised placeholder emails so the field appears empty (no-email logic).
    const rawEmail = chef.email || "";
    const showEmail = rawEmail && !rawEmail.endsWith("@chef.delcochefs.local") && !rawEmail.endsWith("@phone.delcochefs.local");
    setForm({
      name: chef.name || "",
      phone: chef.phone || "",
      email: showEmail ? rawEmail : "",
      specialties: (chef.profile?.specialties || []).join(", "),
      experience_years: chef.profile?.experience_years ?? "",
      address: chef.profile?.address || "",
      notes: chef.profile?.notes || "",
      is_active: chef.is_active !== false,
    });
  }, [chef]);
  if (!chef) return null;
  if (!form) return null;
  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name required"); return; }
    if (form.phone && form.phone.replace(/\D/g, "").length < 10) {
      toast.error("Mobile must contain at least 10 digits"); return;
    }
    setBusy(true);
    try {
      await api.patch(`/admin/chefs/${chef.id}`, {
        name: form.name.trim(),
        phone: form.phone,
        email: form.email.trim() || null,
        specialties: form.specialties.split(",").map(s => s.trim()).filter(Boolean),
        experience_years: Number(form.experience_years) || 0,
        address: form.address,
        notes: form.notes,
        is_active: form.is_active,
      });
      toast.success("Chef updated");
      onSaved && onSaved();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Update failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-chef-dialog">
        <DialogHeader><DialogTitle>Edit chef · {chef?.name || ""}</DialogTitle>
          <DialogDescription>Changes are logged in the admin audit log.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div><Label>Name</Label><Input value={form.name} onChange={e=>setForm({...form, name:e.target.value})} data-testid="edit-chef-name"/></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Mobile</Label>
              <Input
                value={form.phone}
                onChange={e => setForm({...form, phone: e.target.value.replace(/[^\d+]/g, "").slice(0, 15)})}
                onKeyDown={e => { if (!/^[\d+]$/.test(e.key) && !["Backspace","Delete","Tab","ArrowLeft","ArrowRight","Home","End"].includes(e.key)) e.preventDefault(); }}
                placeholder="+91 98765 43210"
                inputMode="tel"
                data-testid="edit-chef-phone"/>
            </div>
            <div><Label>Email <span className="text-xs text-muted-foreground">(optional)</span></Label>
              <Input type="text" value={form.email} onChange={e=>setForm({...form, email:e.target.value})} placeholder="chef@example.com" data-testid="edit-chef-email"/>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Experience (yrs)</Label><Input type="number" value={form.experience_years} onChange={e=>setForm({...form, experience_years:e.target.value})} data-testid="edit-chef-exp"/></div>
            <div><Label>Active</Label>
              <select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={form.is_active ? "yes" : "no"} onChange={e=>setForm({...form, is_active: e.target.value === "yes"})} data-testid="edit-chef-active">
                <option value="yes">Yes</option><option value="no">No (disabled)</option>
              </select>
            </div>
          </div>
          <div><Label>Specialties</Label><Input value={form.specialties} onChange={e=>setForm({...form, specialties:e.target.value})} placeholder="North Indian, Punjabi" data-testid="edit-chef-specialties"/></div>
          <div><Label>Address</Label><Textarea value={form.address} onChange={e=>setForm({...form, address:e.target.value})} data-testid="edit-chef-address"/></div>
          <div><Label>Notes</Label><Textarea value={form.notes} onChange={e=>setForm({...form, notes:e.target.value})} data-testid="edit-chef-notes"/></div>

          {/* Quick Aadhaar viewer inside edit dialog for easy verification */}
          <div className="pt-2 border-t border-black/[0.06]">
            <Label className="text-xs uppercase tracking-wider text-[#1d1d1f]/60">Aadhaar on file</Label>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {["aadhaar_front", "aadhaar_back"].map(key => {
                const has = !!chef.documents?.[key];
                const label = key === "aadhaar_front" ? "Front" : "Back";
                return (
                  <Button
                    key={key} type="button" size="sm" variant="outline"
                    disabled={!has}
                    className="rounded-full text-xs bg-white disabled:opacity-40"
                    onClick={async () => {
                      const ok = await openAuthedInNewTab(`/admin/chefs/${chef.id}/documents/${key}`);
                      if (!ok) toast.error(`Couldn't open Aadhaar ${label}`);
                    }}
                    data-testid={`edit-chef-view-${key}`}
                  >
                    <Eye size={12} className="mr-1"/> Aadhaar {label} {has ? "" : "· not uploaded"}
                  </Button>
                );
              })}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" className="rounded-full" onClick={()=>onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} className="rounded-full bg-[#0071e3]" data-testid="edit-chef-save">{busy ? "Saving…" : "Save changes"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}


// ---- Chef card (auth-loaded photo, edit + docs actions) ----
function ChefCard({ c, isSel, onToggle, onDocs, onEdit }) {
  const [photoUrl, setPhotoUrl] = useState(null);
  const hasPhoto = !!c.documents?.photo;
  useEffect(() => {
    if (!hasPhoto) { setPhotoUrl(null); return; }
    let alive = true;
    let localUrl;
    (async () => {
      const url = await fetchAuthedBlobUrl(`/admin/chefs/${c.id}/documents/photo`);
      if (alive) { localUrl = url; setPhotoUrl(url); }
    })();
    return () => { alive = false; if (localUrl) URL.revokeObjectURL(localUrl); };
  }, [c.id, hasPhoto]);
  const docs = Object.keys(c.documents || {}).length;
  return (
    <div className={`relative rounded-2xl p-5 border transition-shadow ${isSel ? "bg-[#eef6ff] border-[#0071e3]/40 ring-2 ring-[#0071e3]/20" : "bg-[#f5f5f7] border-black/[0.04] hover:shadow-md"}`} data-testid={`chef-card-${c.id}`}>
      <label className="absolute top-3 right-3 flex items-center justify-center w-6 h-6 rounded-md bg-white border border-black/10 cursor-pointer hover:border-[#0071e3]" data-testid={`chef-select-${c.id}`}>
        <input type="checkbox" className="sr-only" checked={isSel} onChange={()=>onToggle(c.id)} />
        {isSel && <Check size={14} className="text-[#0071e3]"/>}
      </label>
      <div className="flex items-start gap-3 pr-8">
        {photoUrl ? (
          <img src={photoUrl} alt={c.name} className="w-14 h-14 rounded-2xl object-cover bg-white" data-testid={`chef-photo-${c.id}`}/>
        ) : (
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#e0f2fe] to-[#dbeafe] flex items-center justify-center">
            <ChefHat size={22} className="text-[#0071e3]"/>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold tracking-tight">{c.name}</h4>
          <p className="text-xs text-[#1d1d1f]/60 truncate">{c.phone}</p>
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between text-xs">
        <span className="text-[#1d1d1f]/60">
          {(c.profile?.specialties || []).slice(0,2).join(" · ") || "—"}
          {c.profile?.experience_years ? ` · ${c.profile.experience_years}y` : ""}
        </span>
        <Badge variant="outline" className="rounded-full text-[10px]">{docs}/6 docs</Badge>
      </div>

      {/* Quick Aadhaar view — one tap to open front/back in a new tab */}
      <div className="mt-3 flex gap-1.5">
        {["aadhaar_front", "aadhaar_back"].map(key => {
          const has = !!c.documents?.[key];
          const label = key === "aadhaar_front" ? "Aadhaar Front" : "Aadhaar Back";
          return (
            <Button
              key={key} size="sm" variant="outline"
              disabled={!has}
              className="flex-1 rounded-full text-[11px] bg-white disabled:opacity-40 disabled:cursor-not-allowed"
              onClick={async (e) => {
                e.stopPropagation();
                const ok = await openAuthedInNewTab(`/admin/chefs/${c.id}/documents/${key}`);
                if (!ok) toast.error(`Couldn't open ${label}`);
              }}
              data-testid={`chef-view-${key}-${c.id}`}
              title={has ? `View ${label}` : `${label} not uploaded`}
            >
              <IdCard size={12} className="mr-1"/> {key === "aadhaar_front" ? "Front" : "Back"}
            </Button>
          );
        })}
      </div>

      <div className="mt-2 flex gap-2">
        <Button size="sm" variant="outline" className="flex-1 rounded-full bg-white text-xs" onClick={() => onDocs(c)} data-testid={`chef-docs-${c.id}`}>
          Documents
        </Button>
        <Button size="sm" variant="outline" className="rounded-full bg-white text-xs" onClick={() => onEdit(c)} data-testid={`chef-edit-${c.id}`}>
          <Pencil size={12} className="mr-1"/>Edit
        </Button>
      </div>
    </div>
  );
}

export default function ChefManagement() {
  const [chefs, setChefs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [docsFor, setDocsFor] = useState(null);
  const [editFor, setEditFor] = useState(null);
  const [selected, setSelected] = useState({});  // {chefId: true}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users", { params: { role: "chef" } });
      setChefs(data);
      setSelected({});
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load chefs"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const selectedIds = Object.keys(selected).filter(k => selected[k]);
  const toggle = (id) => setSelected(s => ({ ...s, [id]: !s[id] }));
  const toggleAll = () => {
    if (selectedIds.length === chefs.length) setSelected({});
    else setSelected(Object.fromEntries(chefs.map(c => [c.id, true])));
  };

  const bulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedIds.length} chef${selectedIds.length === 1 ? "" : "s"}? This cannot be undone.`)) return;
    try {
      const { data } = await api.post("/admin/users/bulk-delete", { ids: selectedIds });
      toast.success(`Deleted ${data.deleted} chef${data.deleted === 1 ? "" : "s"}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Bulk delete failed"); }
  };

  return (
    <div className="rounded-3xl bg-white p-6 sm:p-8 border border-black/[0.06]">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <ChefHat size={20} className="text-[#1d1d1f]/70"/>
          <h3 className="text-2xl font-bold tracking-tight">Chefs</h3>
          <Badge variant="outline" className="rounded-full">{chefs.length}</Badge>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {chefs.length > 0 && (
            <Button onClick={toggleAll} variant="outline" className="rounded-full text-xs" data-testid="chef-select-all">
              {selectedIds.length === chefs.length ? "Clear selection" : "Select all"}
            </Button>
          )}
          {selectedIds.length > 0 && (
            <Button onClick={bulkDelete} className="rounded-full bg-rose-600 hover:bg-rose-700 text-white text-xs" data-testid="chef-bulk-delete">
              <Trash2 size={12} className="mr-1.5"/> Delete {selectedIds.length}
            </Button>
          )}
          <Button onClick={() => setAddOpen(true)} className="rounded-full bg-[#0071e3] hover:bg-[#0077ed] text-white" data-testid="add-chef-btn">
            <Plus size={14} className="mr-1.5"/> Add chef
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-[#1d1d1f]/60">Loading…</p>
      ) : chefs.length === 0 ? (
        <p className="text-sm text-[#1d1d1f]/60 text-center py-10">No chefs yet. Click <b>Add chef</b> to create the first profile.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {chefs.map(c => (
            <ChefCard
              key={c.id}
              c={c}
              isSel={!!selected[c.id]}
              onToggle={toggle}
              onDocs={setDocsFor}
              onEdit={setEditFor}
            />
          ))}
        </div>
      )}

      <AddChefDialog open={addOpen} onOpenChange={setAddOpen} onCreated={load}/>
      <EditChefDialog chef={editFor} open={!!editFor} onOpenChange={(o) => { if (!o) setEditFor(null); }} onSaved={load}/>

      <Dialog open={!!docsFor} onOpenChange={(o) => { if (!o) { setDocsFor(null); load(); } }}>
        <DialogContent className="max-w-2xl" data-testid="docs-dialog">
          <DialogHeader>
            <DialogTitle className="tracking-tight">Documents — {docsFor?.name}</DialogTitle>
            <DialogDescription>Upload Aadhaar, PAN, employment letter and profile photo. Max 8 MB each.</DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            {DOC_TYPES.map(d => docsFor && (
              <DocSlot
                key={d.id} chef={docsFor} doc={d}
                onUploaded={async () => {
                  const { data } = await api.get("/admin/users", { params: { role: "chef" } });
                  const fresh = data.find(x => x.id === docsFor.id);
                  setDocsFor(fresh);
                }}
                onDeleted={async () => {
                  const { data } = await api.get("/admin/users", { params: { role: "chef" } });
                  const fresh = data.find(x => x.id === docsFor.id);
                  setDocsFor(fresh);
                }}
              />
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
